import { Fragment, memo, useState } from "react";
import {
  erTom, medHake, medHuskepunkt, sorterHuskepunkt, type Blokk, type Tekstdok,
} from "@infoflyt/shared/tekst";
import { BESKJED_SLUGER, type Tekstflate } from "../state/types";
import { confirm as hapticConfirm, tap as hapticTap } from "../components/haptics";

/**
 * Beskjeder: det som må sies og gjøres i dag, til dem det gjelder.
 *
 * TRE AVSENDERE PÅ ÉN SIDE. Teknisk crew, innhold og prod skriver hver sitt
 * felt på nettflata, og her står de under hverandre. Beskjedene er korte, og
 * den som leser vil vite om det står noe i dag — ikke lete tre steder.
 *
 * HUSKEPUNKTENE ER DELTE. Huker du av «lade batterier», ser hele crewet at
 * det er gjort. Haken ligger i dokumentet, ikke på telefonen — det er
 * oppførselen en crewliste skal ha.
 *
 * OG DE SKRIVES LOKALT FØRST. Man huker av i en kjeller, og det skal stå der
 * med én gang. Sida legges i utboksen og går opp når dekningen kommer.
 *
 * HUSKEPUNKTENE SAMLES NEDERST, under sin egen overskrift. Et dokument man
 * skal gå gjennom, ikke bare lese: ligger punktene strødd mellom
 * avsnittene, må man lete etter dem — og det er nettopp det man ikke har tid
 * til mellom to opptak.
 *
 * TOMME FELT VISES IKKE. Tre overskrifter med «ingen beskjed» under er støy
 * på en side man skanner.
 */
export const BriefScreen = memo(function BriefScreen({
  flater, onEndret,
}: {
  readonly flater: readonly Tekstflate[];
  onEndret(slug: string, dok: Tekstdok): void;
}): JSX.Element {
  const [skriver, setSkriver] = useState<string | null>(null);
  const [utkast, setUtkast] = useState("");

  /* Rekkefølgen er nettflatas, ikke databasens. */
  const med = BESKJED_SLUGER
    .map((s) => flater.find((f) => f.slug === s))
    .filter((f): f is Tekstflate => f !== undefined);
  const medInnhold = med.filter((f) => !erTom(f.dok));

  const leggTil = (f: Tekstflate): void => {
    const tekst = utkast.trim();
    setUtkast("");
    setSkriver(null);
    if (tekst.length === 0) return;
    hapticConfirm();
    onEndret(f.slug, medHuskepunkt(sorterHuskepunkt(f.dok), tekst));
  };

  if (medInnhold.length === 0 && skriver === null) {
    return (
      <div className="surface surface--rull" style={{ height: "100%" }}>
        <div className="beskjed">
          <span className="beskjed__tom-tittel">Ingen beskjeder i dag</span>
          <span className="beskjed__tom mono">
            Teknisk crew, innhold og prod skriver her fra nettsiden. Kommer
            det noe, står det her — også uten dekning.
          </span>
          {med.map((f) => (
            <button
              key={f.slug}
              type="button"
              className="beskjed__legg-til"
              onClick={() => { hapticTap(); setSkriver(f.slug); }}
            >
              + Huskepunkt til {f.tittel.toLowerCase()}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="surface surface--rull" style={{ height: "100%" }}>
      <div className="beskjed">
        {med.map((f) => {
          const tom = erTom(f.dok);
          if (tom && skriver !== f.slug) return null;
          return (
            <section key={f.slug} className="beskjed__bolk">
              <h2 className="beskjed__til">{f.tittel}</h2>
              <span className="beskjed__naar mono">{naarEndret(f.endretIso)}</span>

              {/*
                Sortert HER, ikke bare naar nettflata lagrer. Da grupperes
                ogsaa det som ble skrevet foer sorteringen fantes — og fordi
                avhukingen lagrer den sorterte utgaven, retter dokumentet
                seg selv foerste gang noen tar i det.
              */}
              {sorterHuskepunkt(f.dok).map((b, i, alle) => (
                <Fragment key={i}>
                  {b.type === "huskepunkt" && alle[i - 1]?.type !== "huskepunkt" ? (
                    <span className="beskjed__huskelabel">Huskeliste</span>
                  ) : null}
                  {blokk(b, i, () => {
                    hapticTap();
                    onEndret(f.slug, medHake(sorterHuskepunkt(f.dok), i, b.gjort !== true));
                  })}
                </Fragment>
              ))}

              {skriver === f.slug ? (
                <div className="beskjed__nytt">
                  <textarea
                    className="beskjed__felt"
                    value={utkast}
                    onChange={(e) => setUtkast(e.target.value)}
                    placeholder="Hva må huskes?"
                    rows={2}
                    autoFocus
                  />
                  <div className="beskjed__nytt-knapper">
                    <button
                      type="button"
                      className="beskjed__avbryt"
                      onClick={() => { setUtkast(""); setSkriver(null); }}
                    >
                      Avbryt
                    </button>
                    <button
                      type="button"
                      className="beskjed__lagre"
                      onClick={() => leggTil(f)}
                    >
                      Legg til
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="beskjed__legg-til"
                  onClick={() => { hapticTap(); setSkriver(f.slug); }}
                >
                  + Huskepunkt
                </button>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
});

/**
 * Punktene samles, resten står for seg.
 *
 * Huskepunktet er en KNAPP: det er den ene blokka man gjør noe med. De andre
 * er tekst, og en knapp rundt tekst man bare skal lese er en løgn om hva
 * flata gjør.
 */
function blokk(b: Blokk, i: number, veksle: () => void): JSX.Element {
  const innhold = b.biter.map((bit, j) => (
    <span
      key={j}
      data-farge={bit.farge}
      style={{
        fontWeight: bit.fet === true ? 600 : undefined,
        fontStyle: bit.kursiv === true ? "italic" : undefined,
        textDecorationLine: [
          bit.understrek === true ? "underline" : "",
          bit.gjennomstrek === true ? "line-through" : "",
        ].filter(Boolean).join(" ") || undefined,
      }}
    >
      {bit.tekst}
    </span>
  ));

  switch (b.type) {
    case "overskrift":
      return <h3 key={i} className="beskjed__h1">{innhold}</h3>;
    case "underoverskrift":
      return <h4 key={i} className="beskjed__h2">{innhold}</h4>;
    case "punkt":
      return (
        <p key={i} className="beskjed__punkt">
          <span className="beskjed__kule" aria-hidden="true">•</span>
          <span>{innhold}</span>
        </p>
      );
    case "huskepunkt":
      return (
        <button
          key={i}
          type="button"
          className={`beskjed__huske${b.gjort === true ? " beskjed__huske--gjort" : ""}`}
          onClick={veksle}
          aria-pressed={b.gjort === true}
        >
          <span className="beskjed__boks" aria-hidden="true">
            {b.gjort === true ? "✓" : ""}
          </span>
          <span>{innhold}</span>
        </button>
      );
    default:
      return <p key={i} className="beskjed__avsnitt">{innhold}</p>;
  }
}

/** «oppdatert 14:12» når det er i dag, ellers med dato. */
function naarEndret(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const tid = d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === new Date().toDateString()) return `oppdatert ${tid}`;
  return `oppdatert ${d.toLocaleDateString("nb-NO", { day: "numeric", month: "short" })} ${tid}`;
}
